import {
  addMapMarkers,
  clearMapMarkers,
  GeoLocation,
  initializeMap,
  loadGoogleMaps,
  updateCurrentPositionPin
} from "../Google/Maps/google-maps";
import { initializePopovers } from "../Shared/Popover/popover";
import { notEmpty } from "../Shared/Utilities/filters";
import { isNullOrWhiteSpace } from "../Shared/Utilities/string";

declare global {
  interface Window {
    ani_googleMapsLoaded?: boolean;
    ani_findClinicMaps?: Map<string, FindClinicMap>;
  }
}

type FindClinicMap = {
  updateClinics: () => Promise<void>;
  updateCurrentPosition: (position: GeoLocation) => Promise<void>;
};

type Clinic = {
  contentId: string;
  id: number;
  name: string;
  region?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  /**
   * 0: Clinic
   * 1: EmergencyClinic
   * 2: ReferralClinic
   * 4: Online
   */
  type: number[];
  url: string;
};

if (!window.ani_findClinicMaps) {
  window.ani_findClinicMaps = new Map();
}

const findClinicsMaps = window.ani_findClinicMaps;

const getMap = (mapId: string) => {
  const map = findClinicsMaps.get(mapId);

  if (!map) {
    throw `Map with id does not exist ${mapId}`;
  }

  return map;
};

export const initializeFindClinicMap = async (rootElement: HTMLElement) => {
  let currentPosition: GeoLocation | undefined = undefined;

  const mapCollapsible = rootElement.querySelector<HTMLElement>(
    "[data-find-clinic-map-collapsible]"
  );

  const mapContainer = rootElement.querySelector<HTMLElement>(
    "[data-find-clinic-map-container]"
  );

  const mapButton = document.querySelector<HTMLElement>(
    `[aria-controls="${mapCollapsible?.id}"]`
  );

  if (!mapCollapsible) {
    throw "Find clinic map collapsible missing";
  }

  if (!mapContainer) {
    throw "Find clinic map container missing";
  }

  if (!mapButton) {
    throw "Find clinic mapButton missing";
  }

  const googleMapsApiKey = rootElement.dataset.apiKey;

  const googleMapsLanguage = rootElement.dataset.language;

  const mapId = rootElement.id;

  const defaultLatitude = !isNullOrWhiteSpace(rootElement.dataset.defaultLatitude)
    ? parseFloat(rootElement.dataset.defaultLatitude)
    : 0;

  const defaultLongitude = !isNullOrWhiteSpace(rootElement.dataset.defaultLongitude)
    ? parseFloat(rootElement.dataset.defaultLongitude)
    : 0;

  const userLatitude = !isNullOrWhiteSpace(rootElement.dataset.userLatitude)
    ? parseFloat(rootElement.dataset.userLatitude)
    : null;

  const userLongitude = !isNullOrWhiteSpace(rootElement.dataset.userLongitude)
    ? parseFloat(rootElement.dataset.userLongitude)
    : null;

  const defaultZoom = !isNullOrWhiteSpace(rootElement.dataset.defaultZoom)
    ? parseInt(rootElement.dataset.defaultZoom)
    : 0;

  if (!mapId) {
    throw "Find clinic mapId missing";
  }

  if (!googleMapsApiKey) {
    throw "Find clinic page googleMapsApiKey missing";
  }

  if (!googleMapsLanguage) {
    throw "Find clinic page googleMapsLanguage missing";
  }

  await loadGoogleMaps(googleMapsApiKey, googleMapsLanguage);

  let mapInitialized = false;

  let clinicsJson = document.querySelector("[data-clinics-json]")?.textContent;

  let clinics = clinicsJson ? (JSON.parse(clinicsJson) as Clinic[]) : undefined;

  if (userLatitude !== null && userLongitude !== null) {
    currentPosition = {
      latitude: userLatitude,
      longitude: userLongitude
    };
  }

  const updateMapMarkers = async () => {
    if (clinics) {
      // Clear existing map markers before adding new ones. Otherwise the clinic-marker-xxx selector
      // ends up using the old markers.
      clearMapMarkers(mapId);

      const markers = clinics.map(clinic => {
        const { latitude, longitude } = clinic.coordinates ?? {};

        if (
          latitude !== undefined &&
          latitude !== 0 &&
          longitude !== undefined &&
          longitude !== 0
        ) {
          const marker: google.maps.marker.AdvancedMarkerElementOptions = {
            content: document.getElementById(`clinic-marker-${clinic.id}`),
            position: {
              lat: latitude,
              lng: longitude
            },
            title: clinic.name
          };

          return marker;
        }
      });

      await addMapMarkers(mapId, markers.filter(notEmpty));
    }
  };

  mapButton.addEventListener("click", async () => {
    if (!mapInitialized) {
      mapInitialized = true;

      const map = await initializeMap(
        mapContainer,
        mapId,
        defaultLatitude,
        defaultLongitude,
        defaultZoom
      );

      // tilesloaded seems to be the most reliable event to detect if the map is completely loaded
      map.addListener("tilesloaded", () => {
        initializePopovers(mapContainer);
      });

      await updateMapMarkers();

      if (currentPosition && currentPosition.latitude && currentPosition.longitude) {
        const pos = {
          lat: currentPosition.latitude,
          lng: currentPosition.longitude
        };

        await updateCurrentPositionPin(mapId, pos);
      }
    }
  });

  findClinicsMaps.set(mapId, {
    updateCurrentPosition: async (position: GeoLocation) => {
      currentPosition = position;

      if (mapInitialized) {
        const pos = {
          lat: currentPosition.latitude,
          lng: currentPosition.longitude
        };

        await updateCurrentPositionPin(mapId, pos);
      }
    },
    updateClinics: async () => {
      // Update Google Maps after searching
      clinicsJson = document.querySelector("[data-clinics-json]")?.textContent;

      clinics = clinicsJson ? (JSON.parse(clinicsJson) as Clinic[]) : undefined;

      if (mapInitialized) {
        await updateMapMarkers();

        // There's no marker initialized event, so we have to use an arbitrary timeout here before (re)initializing
        // popovers.
        setTimeout(() => initializePopovers(mapContainer), 300);
      }
    }
  });
};

export const updateCurrentPosition = async (mapId: string, position: GeoLocation) => {
  const map = getMap(mapId);

  await map.updateCurrentPosition(position);
};

export const updateClinics = async (mapId: string) => {
  const map = getMap(mapId);

  await map.updateClinics();
};

document
  .querySelectorAll<HTMLElement>("[data-find-clinic-map]")
  .forEach(initializeFindClinicMap);
